import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useZakeke } from '@zakeke/zakeke-configurator-react';
import { ReactComponent as NoneIcon } from '../../assets/svg/ban-solid.svg';
import { ReactComponent as CameraIcon } from '../../assets/svg/camera-solid.svg';
import { MOBILE_BREAKPOINT } from '../Store';
import type { PanelDataState, ImageUrlByPresetType, PresetType } from './useAIProductStagingData';
import { useIsMobile } from './hooks';

const getFileNameFromUrl = (url: string): string => {
	try {
		const urlObj = new URL(url);
		const pathname = urlObj.pathname;
		const fileName = pathname.split('/').pop() || 'uploaded-image';
		return decodeURIComponent(fileName);
	} catch (e) {
		return 'uploaded-image';
	}
};

interface OptionsPanelContentProps {
	activePanel: string | null;
	options: {
		background?: PanelDataState;
		surface?: PanelDataState;
		mood?: PanelDataState;
		effect?: PanelDataState;
		person?: PanelDataState;
		pose?: PanelDataState;
	};
	isLoading?: boolean;
	selectedValues: {
		background?: number | null;
		surface?: number | null;
		mood?: number | null;
		effect?: number | null;
		person?: number | null;
		pose?: number | null;
	};
	onSelect: (type: PresetType, value: number | null) => void;
	imageUrl?: ImageUrlByPresetType;
	setImageUrl?: React.Dispatch<React.SetStateAction<ImageUrlByPresetType>>;
	handleUpload?: (presetType: PresetType) => void;
	supportsUpload?: boolean;
	required?: boolean;
	allowCustomBackground?: boolean;
	modalType?: 'product-staging' | 'virtual-tryon';
	menuId: string;
	onWebcamOpen?: () => void;
}

export const OptionsPanelContent: React.FC<OptionsPanelContentProps> = ({
	activePanel,
	options,
	isLoading = false,
	selectedValues,
	onSelect,
	imageUrl,
	setImageUrl,
	handleUpload,
	supportsUpload,
	required = false,
	allowCustomBackground = true,
	modalType = 'product-staging',
	menuId,
	onWebcamOpen,
}) => {
	const { T_static } = useZakeke();
	const T = useMemo(() => ({ _: T_static }), [T_static]);
	const isMobile = useIsMobile();

	// Check if webcam is supported
	const isWebcamSupported =
		typeof navigator !== 'undefined' &&
		navigator.mediaDevices &&
		typeof navigator.mediaDevices.getUserMedia !== 'undefined';

	if (!activePanel) return null;

	if (isLoading) {
		return <SkeletonGrid count={6} type='item' />;
	}

	const panelOptions = options[activePanel as keyof typeof options];
	const items = panelOptions?.presets;
	const categories = panelOptions?.categories;

	if (!items) return null;

	const itemsWithoutCategory = items.filter((item) => !item.categoryId);
	const itemsByCategory: { [categoryId: string]: typeof items } = {};

	items.forEach((item) => {
		if (item.categoryId) {
			if (!itemsByCategory[item.categoryId]) {
				itemsByCategory[item.categoryId] = [];
			}
			itemsByCategory[item.categoryId].push(item);
		}
	});

	const renderPresetBox = (item: (typeof items)[number]) => {
		const isSelected = selectedValues[activePanel as keyof typeof selectedValues] === item.id;

		return (
			<ItemButton
				key={item.id}
				onClick={() => {
					setImageUrl?.((prev) => ({
						...prev,
						[activePanel as PresetType]: {
							...prev[activePanel as PresetType],
							selectedUrl: '',
						},
					}));
					onSelect(activePanel as PresetType, item.id);
				}}
				className={isSelected ? 'selected' : ''}
				role='option'
				aria-selected={isSelected}
				aria-label={item.name}
			>
				<ItemThumbnail>
					<img src={item.previewUrl ?? ''} alt={item.name} />
				</ItemThumbnail>
				<ItemLabel>{item.name}</ItemLabel>
			</ItemButton>
		);
	};

	return (
		<GridContainer>
			<GridWrapper role='listbox' aria-label={`${activePanel} options`} id={menuId}>
				{!required && (
					<ItemButton
						onClick={() => onSelect(activePanel as PresetType, null)}
						className={
							!selectedValues[activePanel as keyof typeof selectedValues] &&
							!imageUrl?.[activePanel as PresetType]?.selectedUrl
								? 'selected'
								: ''
						}
						role='option'
						aria-selected={
							!selectedValues[activePanel as keyof typeof selectedValues] &&
							!imageUrl?.[activePanel as PresetType]?.selectedUrl
						}
						aria-label={T._('None')}
					>
						<EmptyThumbnail>
							<NoneIcon />
						</EmptyThumbnail>
						<ItemLabel>{T._('None')}</ItemLabel>
					</ItemButton>
				)}
				{imageUrl?.[activePanel as PresetType]?.uploadedUrls?.map((uploadedImage, index) => {
					const fileName = getFileNameFromUrl(uploadedImage.imageUrl);
					const isSelected = imageUrl?.[activePanel as PresetType]?.selectedUrl === uploadedImage.imageUrl;

					return (
						<ItemButton
							key={`uploaded-${index}`}
							onClick={() => {
								onSelect(activePanel as PresetType, null);
								if (setImageUrl) {
									setImageUrl((prev) => ({
										...prev,
										[activePanel as PresetType]: {
											...prev[activePanel as PresetType],
											selectedUrl: uploadedImage.imageUrl,
										},
									}));
								}
							}}
							className={isSelected ? 'selected' : ''}
							role='option'
							aria-selected={isSelected}
							aria-label={fileName}
						>
							<ItemThumbnail>
								<img src={uploadedImage.imageUrl} alt={fileName} />
							</ItemThumbnail>
							<ItemLabel title={fileName}>{fileName}</ItemLabel>
						</ItemButton>
					);
				})}
				{/* Webcam button */}
				{isWebcamSupported &&
					!isMobile &&
					onWebcamOpen &&
					modalType === 'virtual-tryon' &&
					activePanel === 'person' && (
						<ItemButton onClick={onWebcamOpen} role='button' aria-label={T._('Use Webcam')}>
							<WebcamThumbnail>
								<CameraIcon aria-hidden='true' />
							</WebcamThumbnail>
							<ItemLabel>{T._('Webcam')}</ItemLabel>
						</ItemButton>
					)}
				{/* Upload button */}
				{supportsUpload &&
					handleUpload &&
					(activePanel === 'background' ? allowCustomBackground : true) && (
						<ItemButton
							onClick={() => handleUpload(activePanel as any)}
							role='button'
							aria-label={T._('Upload')}
						>
							<UploadThumbnail>+</UploadThumbnail>
							<ItemLabel>{T._('Upload')}</ItemLabel>
						</ItemButton>
					)}
			</GridWrapper>
			<GridWrapper role='listbox' aria-label={`${activePanel} presets`}>
				{itemsWithoutCategory.map(renderPresetBox)}
				{categories?.map((category) => {
					const categoryItems = itemsByCategory[category.id];
					if (!categoryItems || categoryItems.length === 0) return null;

					return (
						<React.Fragment key={category.id}>
							<CategorySeparator>
								<CategoryLabel>{category.name}</CategoryLabel>
							</CategorySeparator>
							{categoryItems.map(renderPresetBox)}
						</React.Fragment>
					);
				})}
			</GridWrapper>
		</GridContainer>
	);
};

const GridContainer = styled.div`
	max-height: 400px;
	overflow-y: auto;
	overflow-x: hidden;

	@media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
		max-height: none;
	}

	&::-webkit-scrollbar {
		width: 6px;
	}
	&::-webkit-scrollbar-track {
		background: transparent;
	}
	&::-webkit-scrollbar-thumb {
		background: #d1d5db;
		border-radius: 3px;
	}
	&::-webkit-scrollbar-thumb:hover {
		background: #9ca3af;
	}
`;

const GridWrapper = styled.div`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 8px;
	padding-bottom: 8px;
	align-items: start;
`;

const ItemButton = styled.button`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.5rem;
	transition: all 0.2s;
	background: white;
	border: 1px solid transparent;
	border-radius: 8px;
	padding: 8px;
	cursor: pointer;
	width: 100%;

	&:hover {
		background: #f3f4f6;
		border: 1px solid #e5e7eb;
		box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
	}
`;

const ItemThumbnail = styled.div`
	width: 100%;
	aspect-ratio: 1;
	border-radius: 0.5rem;
	overflow: hidden;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.selected & {
		box-shadow:
			0 0 0 2px white,
			0 0 0 4px #a855f7;
	}
`;

// Webcam thumbnail in grid
const WebcamThumbnail = styled.div`
	width: 100%;
	aspect-ratio: 1;
	border-radius: 0.5rem;
	border: 2px dashed #d1d5db;
	display: flex;
	align-items: center;
	justify-content: center;

	svg {
		width: 2rem;
		height: 2rem;
		color: #9ca3af;
		fill: currentColor;
	}
`;

const UploadThumbnail = styled.div`
	width: 100%;
	aspect-ratio: 1;
	border-radius: 0.5rem;
	border: 2px dashed #d1d5db;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 1.5rem;
	color: #9ca3af;
`;

const EmptyThumbnail = styled.div`
	width: 100%;
	aspect-ratio: 1;
	border-radius: 0.5rem;
	border: 2px dashed #d1d5db;
	display: flex;
	align-items: center;
	justify-content: center;

	svg {
		width: 2rem;
		height: 2rem;
		color: #9ca3af;
	}

	.selected & {
		box-shadow:
			0 0 0 2px white,
			0 0 0 4px #a855f7;
	}
`;

const ItemLabel = styled.span`
	font-size: 0.75rem;
	font-weight: 500;
	color: #4b5563;
	text-align: center;
	line-height: 1.2;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	line-clamp: 2;
	height: 28px;
	font-size: 11px;
`;

const CategorySeparator = styled.div`
	grid-column: 1 / -1;
	display: flex;
	align-items: center;
	justify-content: flex-start;
	padding: 1rem 0 0.5rem 0;
	margin-top: 0.5rem;
	border-top: 1px solid #e5e7eb;

	&:first-child {
		margin-top: 0;
		border-top: none;
		padding-top: 0;
	}
`;

const CategoryLabel = styled.span`
	font-size: 0.6875rem;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.05em;
`;

interface SkeletonGridProps {
	count: number;
	type: 'item' | 'category';
}

const SkeletonGrid: React.FC<SkeletonGridProps> = ({ count, type }) => {
	return (
		<>
			{Array.from({ length: count }).map((_, index) => (
				<React.Fragment key={`skeleton-${index}`}>
					{type === 'item' ? <SkeletonItem /> : <SkeletonCategory />}
				</React.Fragment>
			))}
		</>
	);
};

const SkeletonItem = () => (
	<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', width: '100%' }}>
		<SkeletonThumbnail />
		<SkeletonLabel />
	</div>
);

const SkeletonCategory = () => <SkeletonCategoryTag />;

const SkeletonThumbnail = styled.div`
	width: 100%;
	aspect-ratio: 1;
	border-radius: 0.5rem;
	background-color: #e5e7eb;
	animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;

	@keyframes pulse {
		0%,
		100% {
			opacity: 1;
		}
		50% {
			opacity: 0.5;
		}
	}
`;

const SkeletonLabel = styled.div`
	width: 80%;
	height: 0.75rem;
	border-radius: 0.25rem;
	background-color: #e5e7eb;
	animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
`;

const SkeletonCategoryTag = styled.div`
	grid-column: 1 / -1;
	padding: 0.5rem 1rem;
	border-radius: 0.25rem;
	background-color: #e5e7eb;
	width: 8rem;
	height: 1.5rem;
	animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
`;
